import { createContext, useContext } from 'react';

import type { PageMeta } from './page-meta';

/**
 * SSR meta-collection channel (SSG support).
 *
 * The marketing site's per-page SEO meta is declared *inside* each route via
 * `useSeoMeta()`, which keeps the meta colocated with the page (one source, no
 * parallel registry that could drift). To prerender each route's real `<head>`
 * at build time, the prerender (`scripts/prerender-marketing.ts`) needs that
 * computed meta after `renderToString`.
 *
 * `entry-server.tsx` provides a collector via this context; `useSeoMeta` calls
 * it synchronously during render when present. On the **client** the context is
 * never provided (the browser entry mounts the app without it), so `useSeoMeta`
 * keeps its DOM-injection behaviour unchanged and pays zero cost.
 */
export type SeoCollector = (meta: PageMeta) => void;

const SeoCollectorContext = createContext<SeoCollector | null>(null);

export const SeoCollectorProvider = SeoCollectorContext.Provider;

/** Returns the active SSR collector, or `null` on the client. */
export function useSeoCollector(): SeoCollector | null {
  return useContext(SeoCollectorContext);
}
